import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  Typography,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

interface BulkRejectionError {
  channel_id: string;
  error: string;
}

interface BulkRejectionResultsModalProps {
  open: boolean;
  result: {
    rejected: number;
    failed: number;
    errors: BulkRejectionError[];
  } | null;
  onClose: () => void;
}

export const BulkRejectionResultsModal: React.FC<BulkRejectionResultsModalProps> = ({
  open,
  result,
  onClose,
}) => {
  if (!result) return null;

  const totalChannels = result.rejected + result.failed;
  const successRate = totalChannels > 0 ? Math.round((result.rejected / totalChannels) * 100) : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Bulk Rejection Results</DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* Summary Cards */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
            }}
          >
            {/* Rejected Count */}
            <Box
              sx={{
                p: 2,
                border: '1px solid #4caf50',
                borderRadius: 1,
                backgroundColor: '#f1f8e9',
                textAlign: 'center',
              }}
            >
              <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 32, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#4caf50' }}>
                {result.rejected}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Successfully Rejected
              </Typography>
            </Box>

            {/* Failed Count */}
            <Box
              sx={{
                p: 2,
                border: '1px solid #f44336',
                borderRadius: 1,
                backgroundColor: '#ffebee',
                textAlign: 'center',
              }}
            >
              <ErrorIcon sx={{ color: '#f44336', fontSize: 32, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#f44336' }}>
                {result.failed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Failed
              </Typography>
            </Box>
          </Box>

          {/* Success Rate */}
          <Box
            sx={{
              p: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Success Rate
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {successRate}%
            </Typography>
            <Box
              sx={{
                width: '100%',
                height: 8,
                backgroundColor: '#e0e0e0',
                borderRadius: 1,
                mt: 1,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${successRate}%`,
                  backgroundColor: successRate === 100 ? '#4caf50' : successRate >= 50 ? '#ff9800' : '#f44336',
                  transition: 'width 0.3s ease',
                }}
              />
            </Box>
          </Box>

          {/* Errors Table (if any) */}
          {result.errors.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Errors ({result.errors.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Channel ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.errors.slice(0, 10).map((error, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {error.channel_id.substring(0, 8)}...
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={error.error} size="small" variant="outlined" color="error" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {result.errors.length > 10 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    +{result.errors.length - 10} more errors
                  </Typography>
                )}
              </Box>
            </>
          )}

          {/* Success Alert */}
          {result.rejected > 0 && (
            <Alert severity={result.failed === 0 ? 'success' : 'warning'}>
              {result.failed === 0
                ? `All ${result.rejected} channels have been rejected successfully!`
                : `${result.rejected} channels rejected, ${result.failed} failed. Please review the errors above.`}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
